/**
 * WeeklyReportListRow — T-27 summary row for the Weekly Reports list (full mode of
 * WeeklyReportSection). One row per report, newest week first.
 *
 * Row content and data source (all from GET /api/projects/{projectId}/weekly-reports, except
 * where noted):
 *   - Week ending + lifecycle chip (Draft / Submitted)      → `week_ending`, `status`
 *   - Overall status badge                                 → `overall_status`
 *   - RAG trio (Scope / Schedule / Budget)                 → `scope`, `schedule`, `budget_status`
 *     plus Escalation, shown only when it is red           → `escalation`
 *   - Review chip ("—" for drafts / never-reviewed rows)   → `review_status` (V052)
 *   - Cumulative hours, "of {sow}" when known              → `cumulative_hours`; SOW hours from
 *                                                             the project (`useProject`)
 *   - Phase at submission; falls back to the project's     → `current_phase` on the report, else
 *     current phase, marked "(current)"                      the project's `current_phase`
 *   - First risk line, truncated; full text in `title`     → `risks`
 *
 * Colour is never the only signal: the overall badge has an aria-label + tooltip, every RAG pill
 * shows the status word and carries an accessible name such as "Schedule: Yellow".
 *
 * Copy for this list lives in `weeklyReportListMessages` below (same keyed shape as
 * weekly-report-messages.ts, so it can be merged into that catalogue later).
 */
import { CalendarDays } from "lucide-react";

import {
  isDraftReport,
  type WeeklyReportListFilter,
} from "@/hooks/useWeeklyReports";
import type {
  EscalationStatus,
  RagStatus,
  WeeklyReport,
  WeeklyReportStatus,
} from "@/types";
import { Badge, type BadgeVariant } from "@/components/ui/badge";
import { Tooltip } from "@/components/ui/tooltip";
import { phaseLabel } from "@/lib/phases";
import { weeklyReportMessages as msg } from "./weekly-report-messages";
import { ReviewStatusBadge } from "./WeeklyReportReview";

// ─── Copy ─────────────────────────────────────────────────────────────────────

export const weeklyReportListMessages = {
  filterLabel: "Review status",
  filterOptions: {
    all: "All",
    pending: "Awaiting review",
    approved: "Approved",
    changes_requested: "Changes requested",
    draft: "Drafts",
  } as Record<WeeklyReportListFilter, string>,
  emptyFilter: "No reports match this filter.",
  submittedBadge: "Submitted",
  /** Screen-reader text behind the visible "—" review placeholder. */
  noReview: "Review status: none",
  hoursLabel: "Hours",
  hoursValue: (cumulative: string, sow: string | null): string =>
    sow ? `${cumulative} of ${sow}` : cumulative,
  phaseLabel: "Phase",
  phaseFallbackSuffix: "(current)",
  phaseFallbackTitle:
    "Not recorded on this report — showing the project's current phase",
  riskLabel: "Risk",
  ragName: (dimension: string, value: string): string =>
    `${dimension}: ${value}`,
  notReported: "not reported",
} as const;

const L = weeklyReportListMessages;

/** Max characters of the first risk line shown in a row before the ellipsis. */
export const RISK_LINE_MAX = 120;

// ─── Status presentation (shared with WeeklyReportSection) ───────────────────

export const OVERALL_STATUS_META: Record<
  WeeklyReportStatus,
  { label: string; tooltip: string; variant: BadgeVariant }
> = {
  on_track: { ...msg.overallStatus.on_track, variant: "success" },
  at_risk: { ...msg.overallStatus.at_risk, variant: "warning" },
  off_track: { ...msg.overallStatus.off_track, variant: "danger" },
};

// Escalation: red/green only — no yellow
export const ESCALATION_META: Record<
  EscalationStatus,
  { label: string; className: string }
> = {
  green: {
    label: "Green",
    className: "bg-green-50 text-green-800 border-green-200",
  },
  red: { label: "Red", className: "bg-red-50 text-red-800 border-red-200" },
};

// Schedule / Scope / Budget: green/yellow/red
export const RAG_META: Record<RagStatus, { label: string; className: string }> =
  {
    green: {
      label: "Green",
      className: "bg-green-50 text-green-800 border-green-200",
    },
    yellow: {
      label: "Yellow",
      className: "bg-amber-50 text-amber-800 border-amber-200",
    },
    red: { label: "Red", className: "bg-red-50 text-red-800 border-red-200" },
  };

/** Locale date for a report timestamp / YYYY-MM-DD ("—" when absent). */
export function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString();
}

/**
 * Overall project status indicator — a colour-coded badge with a tooltip on hover/focus.
 *
 * The green/yellow/red colour is meaningful, so it is not left as the only carrier of that
 * meaning: the tooltip text is also set as the badge's `aria-label` (WCAG 1.4.1). `tabIndex={0}`
 * makes the indicator focusable so the tooltip is reachable by keyboard, not hover only.
 *
 * Tooltip copy: green → "On Track", yellow → "At Risk", red → "Off Track / Escalated".
 */
export function OverallStatusBadge({
  status,
}: {
  /** NULL only on a draft (V047) — renders nothing rather than a misleading default. */
  status: WeeklyReportStatus | null;
}): JSX.Element | null {
  if (!status) return null;
  const meta = OVERALL_STATUS_META[status];
  if (!meta) return null;
  return (
    <Tooltip text={meta.tooltip}>
      <Badge variant={meta.variant} aria-label={meta.tooltip} tabIndex={0}>
        {meta.label}
      </Badge>
    </Tooltip>
  );
}

export function RagDot({
  status,
  escalationOnly,
}: {
  status: string | null;
  escalationOnly?: boolean;
}): JSX.Element {
  if (!status) return <span className="text-text-secondary text-sm">—</span>;
  const meta = escalationOnly
    ? ESCALATION_META[status as EscalationStatus]
    : RAG_META[status as RagStatus];
  if (!meta) return <span className="text-sm">{status}</span>;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-xs font-medium ${meta.className}`}
    >
      {/* Decorative — the adjacent text label already names the status. */}
      <span
        aria-hidden="true"
        className={`h-2 w-2 rounded-full ${status === "green" ? "bg-green-500" : status === "yellow" ? "bg-yellow-400" : "bg-red-500"}`}
      />
      {meta.label}
    </span>
  );
}

// ─── Pure helpers ─────────────────────────────────────────────────────────────

/**
 * First non-blank line of the risks narrative, capped at `max` characters. An ellipsis is
 * appended when the line was cut OR further lines follow, so the row never implies it is the
 * whole entry. Returns null when nothing was recorded.
 */
export function firstRiskLine(
  risks: string | null | undefined,
  max: number = RISK_LINE_MAX,
): string | null {
  const lines = (risks ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const [first] = lines;
  if (!first) return null;
  if (first.length > max) return `${first.slice(0, max - 1).trimEnd()}…`;
  return lines.length > 1 ? `${first}…` : first;
}

/** `cumulative_hours` arrives as a Postgres numeric (string) or a number; null when unusable. */
function toHours(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// ─── RAG pill ─────────────────────────────────────────────────────────────────

function RagPill({
  testId,
  dimension,
  status,
  escalationOnly,
}: {
  testId: string;
  dimension: string;
  status: string | null;
  escalationOnly?: boolean;
}): JSX.Element {
  const meta = status
    ? escalationOnly
      ? ESCALATION_META[status as EscalationStatus]
      : RAG_META[status as RagStatus]
    : undefined;
  const name = L.ragName(dimension, meta?.label ?? L.notReported);
  return (
    // role="img" gives the compound pill one accessible name ("Schedule: Yellow"); the visible
    // dimension + status word keep it readable without relying on colour.
    <span
      role="img"
      aria-label={name}
      title={name}
      data-testid={testId}
      className="inline-flex items-center gap-1"
    >
      <span className="text-xs text-text-secondary">{dimension}</span>
      <RagDot status={status} escalationOnly={escalationOnly} />
    </span>
  );
}

// ─── Row summary ──────────────────────────────────────────────────────────────

export interface WeeklyReportRowSummaryProps {
  report: WeeklyReport;
  /** The project's SOW hours (from the project record); omitted / null → no "of N". */
  sowHours?: number | null;
  /** The project's current phase — used only when the report did not record one. */
  projectPhase?: string | null;
}

/**
 * The summary content of one list row. Rendered inside the AccordionTrigger for a submitted
 * report (so clicking the row expands it exactly as before) and as the body of a draft row.
 * Contains no interactive controls apart from the focusable overall badge (as before).
 */
export function WeeklyReportRowSummary({
  report,
  sowHours,
  projectPhase,
}: WeeklyReportRowSummaryProps): JSX.Element {
  const draft = isDraftReport(report);
  const hours = toHours(report.cumulative_hours);
  const sow = toHours(sowHours);
  const reportPhase = report.current_phase?.trim() || null;
  const fallbackPhase = !reportPhase ? projectPhase?.trim() || null : null;
  const risk = firstRiskLine(report.risks);

  return (
    <div
      className="flex min-w-0 flex-1 flex-col gap-2 text-left"
      data-testid="weekly-report-row"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <CalendarDays
            className="h-4 w-4 text-text-secondary"
            aria-hidden="true"
          />
          <span className="text-sub-heading text-text-primary">
            {msg.snapshotFields.weekEnding} {fmtDate(report.week_ending)}
          </span>
          <Badge
            variant={draft ? "warning" : "neutral"}
            data-testid="weekly-report-row-lifecycle"
          >
            {draft ? msg.lifecycle.draftBadge : L.submittedBadge}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <OverallStatusBadge status={report.overall_status} />
          <span data-testid="weekly-report-row-review">
            {!draft && report.review_status ? (
              <ReviewStatusBadge status={report.review_status} />
            ) : (
              <>
                <span
                  aria-hidden="true"
                  className="text-sm text-text-secondary"
                >
                  —
                </span>
                <span className="sr-only">{L.noReview}</span>
              </>
            )}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <RagPill
          testId="weekly-report-row-rag-scope"
          dimension={msg.fields.scope}
          status={report.scope}
        />
        <RagPill
          testId="weekly-report-row-rag-schedule"
          dimension={msg.fields.schedule}
          status={report.schedule}
        />
        <RagPill
          testId="weekly-report-row-rag-budget"
          dimension={msg.fields.budget}
          status={report.budget_status}
        />
        {/* Escalation is only worth row space when raised; it stays in the expanded report. */}
        {report.escalation === "red" && (
          <RagPill
            testId="weekly-report-row-rag-escalation"
            dimension={msg.fields.escalation}
            status={report.escalation}
            escalationOnly
          />
        )}
        <span
          className="text-xs text-text-secondary"
          data-testid="weekly-report-row-hours"
        >
          {L.hoursLabel}:{" "}
          <span className="text-text-primary">
            {hours === null
              ? "—"
              : L.hoursValue(
                  hours.toLocaleString(),
                  sow === null ? null : sow.toLocaleString(),
                )}
          </span>
        </span>
        <span
          className="text-xs text-text-secondary"
          data-testid="weekly-report-row-phase"
          title={fallbackPhase ? L.phaseFallbackTitle : undefined}
        >
          {L.phaseLabel}:{" "}
          <span className="text-text-primary">
            {/* Stored values may be legacy labels ("P2 — Design & Review") — show the canonical one. */}
            {reportPhase
              ? phaseLabel(reportPhase)
              : fallbackPhase
                ? `${phaseLabel(fallbackPhase)} ${L.phaseFallbackSuffix}`
                : "—"}
          </span>
        </span>
      </div>

      {risk && (
        <p
          className="truncate text-xs text-text-secondary"
          title={report.risks ?? undefined}
          data-testid="weekly-report-row-risk"
        >
          {L.riskLabel}: <span className="text-text-primary">{risk}</span>
        </p>
      )}
    </div>
  );
}

export default WeeklyReportRowSummary;
